<template>
  <div class="StorageExplorerList DataUpload__Content flex flex-col bg-white rounded-md gap-2 overflow-scroll mb-6 relative">
    <storage-explorer-filters 
      v-model="searchQuery"
      :current-sort-dir="bcFileStore.dir"
      :current-sort-order="bcFileStore.order"
      :sort-filters="sortFilters"
      @update:sort-order="sort"
    />
    <div 
      class="px-2 pb-2"
      :class="view === 'grid' ? 'grid grid-cols-3 sm:grid-cols-6 gap-2' : 'DataUpload__List flex flex-col'"
    >
      <template v-if="view === 'grid' && bcFileStore.list.length > 0">
        <storage-explorer-item-box
          v-for="(item, i) in bcFileStore.list"
          :key="i"
          :is-data-storage="true"
          :is-selected="selectedItems.has(item.id)"
          :item="item"
          :index="i"
          @select="selectItem"
          @unselect="unselectItem"
        />
      </template>
      <template v-else-if="view === 'list'&& bcFileStore.list.length > 0">
        <storage-explorer-item-row
          v-for="(item, i) in bcFileStore.list"
          :key="i"
          :is-data-storage="true"
          :is-selected="selectedItems.has(item.id)"
          :item="item"
          :index="i"
          @select="selectItem"
          @unselect="unselectItem"
        />
      </template>
    </div>
    <div ref="loadMoreEl" class="w-full col-span-3 sm:col-span-6" v-show="showLoadMoreEl">
      &nbsp;
    </div>
    <div 
      v-if="isLoadingInitial" 
      class="absolute inset-0 flex items-center justify-center h-full w-full px-2 mb-4"
    >
      <div class="absolute inset-0 h-full w-full opacity-60 bg-white" />
      <aw-icon icon="loading" class="animate-spin w-6 h-6 md:w-10 md:h-10 leading-tight text-bc-blue" />
    </div>
    <div v-if="isLoadingMore" class="flex items-center justify-center px-2 mb-4">
      <aw-icon icon="loading" class="animate-spin w-6 h-6 md:w-10 md:h-10 leading-tight text-bc-blue" />
    </div>
    <span 
      v-if="isEmpty" 
      class="absolute inset-0 flex items-center justify-center h-full px-2 mb-4 text-gray-300 text-lg pointer-events-none"
    >
      {{ $t('No results') }}
    </span>
  </div>
  <storage-explorer-upload-drop-zone/>
</template>

<script>
import { mapStores } from 'pinia'
import useBcFileStore from '@/frontapp/store/bcFile'
import debounce from 'lodash/debounce'
import StorageExplorerSelected from '../../mixins/StorageExplorerSelected'

let lastSearch

export default {
  mixins: [
    StorageExplorerSelected,
  ],
  props: {
    view: String,
  },
  data() {
    return {
      sortFilters: [
        {
          action: 'name',
          text: 'By name'
        },
        {
          action: 'uploaded',
          text: 'By uploaded'
        },
        {
          action: 'size',
          text: 'By size'
        },
      ],
      loadMoreObserver: null,
      limit: 50,
      debouncedSearch: null,
    }
  },
  computed: {
    ...mapStores(useBcFileStore),
    showLoadMoreEl() {
      return this.bcFileStore.list.length % this.limit > 0 || this.bcFileStore.loading ? false : true
    },
    isLoadingInitial() {
      return this.bcFileStore.list.length === 0 && this.bcFileStore.loading
    },
    isLoadingMore() {
      return this.bcFileStore.list.length > 0 && this.bcFileStore.loading && !this.isLoadingInitial
    },
    isEmpty() {
      return this.bcFileStore.list.length === 0 && !this.isLoadingInitial
    },
    searchQuery: {
      get() {
        return this.bcFileStore.query
      },
      set(val) {
        this.bcFileStore.query = val || val == '' ? val : null
      }
    },
  },
  methods: {
    async loadData(clearSelected = true) {
      this.bcFileStore.loading = true
      if (clearSelected) {
        this.selectedItems.clear()
      }

      await this.bcFileStore.loadFiles()

      this.bcFileStore.loading = false
    },
    mapRequestDataToBc(data) {
      return data.map(item => {
        const lastDotIndex = item.name.lastIndexOf('.')
        
        return {
          id: item.path,
          size: item.fileSize,
          name: item.name,
          uploaded: item.lastModified,
          ext: lastDotIndex !== -1
            ? item.name.substring(lastDotIndex, item.name.length)
            : '',
          type: item.type
        }
      })
    },
    async sort(sortOrder) {
      if (this.bcFileStore.order === sortOrder) {
        if (this.bcFileStore.dir === 'desc') {
          this.bcFileStore.dir = 'asc'
        } else {
          this.bcFileStore.dir = 'desc'
        }
      } else {
        this.bcFileStore.order = sortOrder
      }

      this.bcFileStore.list = []
      this.bcFileStore.offset = 0
      this.loadData()
      this.restartLoadMoreObserver()
    },
    restartLoadMoreObserver() {
      this.loadMoreObserver.unobserve(this.$refs.loadMoreEl)
      this.$nextTick(() => {
        this.loadMoreObserver.observe(this.$refs.loadMoreEl)
      })
    },
    handleIntersect(entries) {
      if (!this.bcFileStore.loading && entries[0].isIntersecting === true) {
        this.bcFileStore.offset += this.bcFileStore.limit
        this.loadData(false)
      }
    },
  },
  mounted() {
    this.bcFileStore.showModalSearch(true, this.limit)
    this.loadData()

    this.loadMoreObserver = new IntersectionObserver(this.handleIntersect)
    this.$nextTick(() => {
      if (this.$refs.loadMoreEl instanceof HTMLElement) {
        this.loadMoreObserver.observe(this.$refs.loadMoreEl)
      }
    })
  },
  beforeUnmount() {
    this.loadMoreObserver.unobserve(this.$refs.loadMoreEl)
    this.loadMoreObserver = null
  },
  watch: {
    view() {
      this.restartLoadMoreObserver()
    },
    'bcFileStore.q': {
      handler() {
        this.bcFileStore.list = []
        this.bcFileStore.offset = 0
        this.loadData()
        this.restartLoadMoreObserver()
      }
    },
    searchQuery(newVal) {
      if (this.debouncedSearch === null) {
        this.debouncedSearch = debounce((search) => {
          if (search === lastSearch) {
            return
          }

          lastSearch = search

          if (search == '') {
            return
          }
        }, 300)
      }

      this.debouncedSearch(newVal)
    },
  }
}
</script>

<style scoped>
.StorageExplorerList {
  height: 100%;
}
</style>
